import { useQuery } from '@tanstack/react-query';
import { orderService } from '@/api/services/OrderService';
import { bestPriceService, type CptForPriceParams } from '@/api/services/BestPriceService';
import { useAuth } from '@/stores/authContext';

export const BUY_TREE_QUERY_KEY = ['buyTree'] as const;

// Дерево моих закупок (server-state). Включено только при авторизации; отмена по signal.
export function useBuyTree() {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: BUY_TREE_QUERY_KEY,
    queryFn: ({ signal }) => orderService.getBuyTree(signal),
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
}

/** Параметры группы прайс-листа (год — внутренний, в UI не показываем). */
export interface PriceListQuery {
  readonly address: string;
  readonly addressTitle: string;
  readonly goods: string;
  /** Год узла дерева от сервера выпуска А; у закупки года нет (решение 2026-11-04). */
  readonly year?: number;
}

// Детальный прайс-лист группы. Запрос включён только при наличии валидных параметров.
export function usePurchasePriceList(query: PriceListQuery | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: ['purchasePriceList', query?.address, query?.addressTitle, query?.goods, query?.year] as const,
    queryFn: ({ signal }) =>
      orderService.getPurchasePriceList(
        {
          address: query!.address,
          addressTitle: query!.addressTitle,
          parentGoodsType: query!.goods,
          cropYear: query!.year,
        },
        signal,
      ),
    enabled: isAuthenticated && query !== null,
    staleTime: 60_000,
  });
}

// Лучшие цены продажи под мои строки закупки (Фаза 2). Мягкий: при ошибке/пустоте грид
// показывает только мои цены. Включён только при наличии orderIds.
export function useBuyBestPrices(params: CptForPriceParams | null) {
  const { isAuthenticated } = useAuth();
  return useQuery({
    queryKey: [
      'buyBestPrices',
      params?.purchaseOrderIds,
      params?.address,
      params?.goodsTypeParent,
      params?.cropYear,
    ] as const,
    queryFn: ({ signal }) => bestPriceService.getCptForPrice(params!, signal),
    enabled: isAuthenticated && params !== null && params.purchaseOrderIds.length > 0,
    staleTime: 60_000,
    retry: false,
  });
}
